// Public /get-estimate page — rough-pricing questionnaire.
//
// Anyone can fill this out without auth. 7 functional questions + 1
// optional free-text + 1 lead-capture form. The estimate range is
// shown BEFORE the lead form so visitors who don't want to leave
// contact info still see a number; the form is opt-in for "want a
// refined quote / a call".
//
// The whole wizard is one component because the question shape is
// simple and adding screens shouldn't require splitting files.

import {
  ArrowLeft,
  ArrowRight,
  CalendarClock,
  Check,
  Loader2,
  Mail,
  Sparkles,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

// Single source of truth for the wizard's question copy + answer
// options. Easy to tweak labels here without touching the rest of
// the component. The `value` strings line up 1:1 with the backend's
// EngagementType / ModalityKey / etc. enums.

const ENGAGEMENT_OPTIONS = [
  {
    value: "keynote",
    label: "Keynote talk",
    desc: "60–90 minute talk to one big audience",
  },
  {
    value: "lunch_learn",
    label: "Lunch & learn",
    desc: "About an hour, smaller group, hands-on tone",
  },
  {
    value: "workshop",
    label: "Workshop",
    desc: "Half- to full-day, working session for one team",
  },
  {
    value: "bootcamp",
    label: "Multi-day bootcamp",
    desc: "Multi-session deep dive across days or weeks",
  },
  {
    value: "exec_briefing",
    label: "Executive briefing",
    desc: "Private session with leadership / board",
  },
  {
    value: "large_workshop",
    label: "Large workshop",
    desc: "Working session for 200+ attendees",
  },
] as const;

const DURATION_OPTIONS = [
  { value: 1, label: "60 minutes" },
  { value: 1.5, label: "90 minutes" },
  { value: 4, label: "Half day (≈4 hours)" },
  { value: 8, label: "Full day (≈8 hours)" },
  { value: 16, label: "Multi-day (2+ days)" },
] as const;

const MODALITY_OPTIONS = [
  { value: "in_person", label: "In person" },
  { value: "virtual", label: "Virtual" },
  { value: "hybrid", label: "Hybrid (both)" },
] as const;

const TRAVEL_OPTIONS = [
  {
    value: "local",
    label: "Boise / Treasure Valley",
    desc: "Drive distance",
  },
  {
    value: "regional",
    label: "Pacific Northwest",
    desc: "Within a few hours' drive or short flight",
  },
  {
    value: "domestic_flight",
    label: "Anywhere in the US",
    desc: "Domestic flight required",
  },
  {
    value: "international",
    label: "International",
    desc: "Outside the US",
  },
] as const;

const LEAD_TIME_OPTIONS = [
  {
    value: "rush_under_1wk",
    label: "This week",
    desc: "Rush — limited availability",
  },
  { value: "short_1_3wk", label: "Next 1–3 weeks" },
  { value: "standard_3_8wk", label: "1–2 months out" },
  { value: "ample_8wk_plus", label: "2+ months out", desc: "Flexible" },
] as const;

const CUSTOMIZATION_OPTIONS = [
  {
    value: "light",
    label: "Off the shelf",
    desc: "Use the standard material as-is",
  },
  {
    value: "moderate",
    label: "Lightly tailored",
    desc: "Industry examples and case studies swapped in",
  },
  {
    value: "deep",
    label: "Custom-built",
    desc: "Curriculum built from scratch with pre-work",
  },
  {
    value: "bespoke",
    label: "Bespoke engagement",
    desc: "Multi-month, weekly drop-ins, internal champion",
  },
] as const;

interface Answers {
  engagement_type?: string;
  group_size?: number;
  delivery_hours?: number;
  modality?: string;
  travel_zone?: string;
  lead_time_band?: string;
  customization?: string;
  message?: string;
}

interface EstimateResponse {
  estimated_low: number;
  estimated_high: number;
  recommended_mid: number;
  notes: string[];
}

// Notion calendar link for the "Schedule a call" CTA — same URL the
// old BookPresentation page used.
const CALENDAR_URL =
  "https://calendar.notion.so/meet/aigeniusconsulting/793ez04oz7";

function currency(n: number): string {
  return `$${Math.round(n).toLocaleString()}`;
}

// Step ids drive the wizard order. We dynamically skip 'travel' when
// modality=virtual so virtual attendees don't see an irrelevant
// question.
type StepId =
  | "engagement"
  | "duration"
  | "modality"
  | "travel"
  | "group_size"
  | "lead_time"
  | "customization"
  | "message"
  | "estimate"
  | "lead";

const ALL_STEPS: StepId[] = [
  "engagement",
  "duration",
  "modality",
  "travel",
  "group_size",
  "lead_time",
  "customization",
  "message",
  "estimate",
  "lead",
];

export default function GetEstimatePage() {
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [estimate, setEstimate] = useState<EstimateResponse | null>(null);
  const [loadingEstimate, setLoadingEstimate] = useState(false);
  const [estimateError, setEstimateError] = useState<string | null>(null);

  // Lead-capture state. Empty by default; the user can submit without
  // leaving any contact info (the wizard treats the final 'Submit'
  // as either "save the lead" or "thanks, anonymously logged").
  const [leadName, setLeadName] = useState("");
  const [leadEmail, setLeadEmail] = useState("");
  const [leadCompany, setLeadCompany] = useState("");
  const [leadPhone, setLeadPhone] = useState("");
  const [honeypot, setHoneypot] = useState(""); // Bots fill this; we drop the submit.

  const [submitting, setSubmitting] = useState(false);
  const [submittedOk, setSubmittedOk] = useState(false);

  // Skip travel step entirely when modality=virtual.
  const steps = useMemo(() => {
    if (answers.modality === "virtual") {
      return ALL_STEPS.filter((s) => s !== "travel");
    }
    return ALL_STEPS;
  }, [answers.modality]);

  // Clamp stepIndex if the list shrinks under us (e.g. user picks
  // virtual after having moved past travel).
  useEffect(() => {
    if (stepIndex >= steps.length) {
      setStepIndex(Math.max(0, steps.length - 1));
    }
  }, [steps.length, stepIndex]);

  const currentStep: StepId = steps[stepIndex];

  // When we land on the 'estimate' step, fetch the rough estimate
  // from the backend. We refetch if the user navigates back and
  // changes an answer.
  useEffect(() => {
    if (currentStep !== "estimate") return;
    let cancelled = false;
    setLoadingEstimate(true);
    setEstimateError(null);
    fetch("/api/pricing-inquiries/estimate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ answers }),
    })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`Estimate failed (${res.status})`);
        }
        return res.json();
      })
      .then((data: EstimateResponse) => {
        if (!cancelled) setEstimate(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setEstimateError(
            err?.message ?? "Could not compute an estimate right now.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingEstimate(false);
      });
    return () => {
      cancelled = true;
    };
    // We want this to re-fire when the user navigates back, changes
    // an answer, and returns to the estimate step.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, JSON.stringify(answers)]);

  // Per-step "can advance" predicate.
  const canAdvance = (): boolean => {
    switch (currentStep) {
      case "engagement":
        return !!answers.engagement_type;
      case "duration":
        return typeof answers.delivery_hours === "number";
      case "modality":
        return !!answers.modality;
      case "travel":
        return !!answers.travel_zone;
      case "group_size":
        return typeof answers.group_size === "number" && answers.group_size > 0;
      case "lead_time":
        return !!answers.lead_time_band;
      case "customization":
        return !!answers.customization;
      case "message":
        return true; // optional
      case "estimate":
        return !!estimate;
      case "lead":
        return true; // optional — user can submit without contact info
    }
  };

  const goNext = () => setStepIndex((i) => Math.min(steps.length - 1, i + 1));
  const goBack = () => setStepIndex((i) => Math.max(0, i - 1));

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch("/api/pricing-inquiries/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          answers,
          estimate,
          lead: {
            name: leadName,
            email: leadEmail,
            company: leadCompany,
            phone: leadPhone,
            message: answers.message,
          },
          honeypot,
        }),
      });
      if (!res.ok) {
        throw new Error(`Submit failed (${res.status})`);
      }
      setSubmittedOk(true);
    } catch (err) {
      // Even if the save failed, the user has seen the estimate — we
      // soft-fail and still show the thanks screen so they aren't
      // stuck. The backend log will catch the real error.
      console.error(err);
      setSubmittedOk(true);
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Rendering helpers ────────────────────────────────────────────
  const renderChoice = (
    options: ReadonlyArray<{
      value: string | number;
      label: string;
      desc?: string;
    }>,
    selected: string | number | undefined,
    onPick: (value: any) => void,
  ) => (
    <div className="grid gap-3 sm:grid-cols-2">
      {options.map((opt) => {
        const isSelected = selected === opt.value;
        return (
          <button
            key={String(opt.value)}
            type="button"
            onClick={() => onPick(opt.value)}
            className={`flex flex-col items-start gap-1 rounded-lg border-2 px-4 py-3 text-left transition ${
              isSelected
                ? "border-[#B87333] bg-[#fdf6ec] shadow-md"
                : "border-[#e8c9a2] bg-white hover:border-[#B87333]/50 hover:bg-[#fdf6ec]/40"
            }`}
          >
            <div className="text-sm font-bold text-[#5a3614]">{opt.label}</div>
            {opt.desc ? (
              <div className="text-xs text-slate-600">{opt.desc}</div>
            ) : null}
          </button>
        );
      })}
    </div>
  );

  if (submittedOk) {
    return (
      <PageShell>
        <div className="mx-auto max-w-xl rounded-lg border-2 border-[#e8c9a2] bg-white p-8 text-center shadow-md">
          <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
            <Check className="h-6 w-6" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-[#5a3614]">
            Thanks — we'll be in touch
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            {leadEmail
              ? `We have everything we need. Look for a follow-up at ${leadEmail} within a business day, or grab time below if you want to talk sooner.`
              : "If you want a refined quote or a quick call, the calendar link below is the fastest path."}
          </p>
          {estimate ? (
            <div className="mt-5 rounded-md border border-[#e8c9a2] bg-[#fdf6ec]/40 p-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#8a531f]">
                Your rough estimate
              </div>
              <div className="text-2xl font-bold text-[#5a3614]">
                {currency(estimate.estimated_low)} –{" "}
                {currency(estimate.estimated_high)}
              </div>
            </div>
          ) : null}
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a
              href={CALENDAR_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md bg-gradient-to-b from-[#B87333] to-[#8a531f] px-4 py-2 text-sm font-bold text-white shadow-md"
            >
              <CalendarClock className="h-4 w-4" />
              Schedule a 15-min call
            </a>
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-md border border-[#e8c9a2] bg-white px-4 py-2 text-sm font-bold text-[#8a531f] shadow-sm hover:bg-[#fdf6ec]"
            >
              Back to home
            </Link>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="mx-auto max-w-2xl">
        {/* Progress dots */}
        <div className="mb-4 flex items-center justify-center gap-1.5">
          {steps.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === stepIndex
                  ? "w-6 bg-[#B87333]"
                  : i < stepIndex
                    ? "w-1.5 bg-[#B87333]/60"
                    : "w-1.5 bg-[#e8c9a2]"
              }`}
            />
          ))}
        </div>

        <div className="rounded-lg border-2 border-[#e8c9a2] bg-white p-6 shadow-md sm:p-8">
          {currentStep === "engagement" ? (
            <Step
              title="What kind of session are you considering?"
              hint="Pick whichever feels closest — we can refine on the call."
            >
              {renderChoice(ENGAGEMENT_OPTIONS, answers.engagement_type, (v) =>
                setAnswers((a) => ({ ...a, engagement_type: v })),
              )}
            </Step>
          ) : null}

          {currentStep === "duration" ? (
            <Step title="How long do you want the session?">
              {renderChoice(DURATION_OPTIONS, answers.delivery_hours, (v) =>
                setAnswers((a) => ({ ...a, delivery_hours: v })),
              )}
            </Step>
          ) : null}

          {currentStep === "modality" ? (
            <Step title="In person, virtual, or both?">
              {renderChoice(MODALITY_OPTIONS, answers.modality, (v) =>
                setAnswers((a) => ({
                  ...a,
                  modality: v,
                  // Clear travel zone if user switches TO virtual.
                  travel_zone: v === "virtual" ? undefined : a.travel_zone,
                })),
              )}
            </Step>
          ) : null}

          {currentStep === "travel" ? (
            <Step
              title="Where will it happen?"
              hint="We're based in Boise; travel beyond drives the cost."
            >
              {renderChoice(TRAVEL_OPTIONS, answers.travel_zone, (v) =>
                setAnswers((a) => ({ ...a, travel_zone: v })),
              )}
            </Step>
          ) : null}

          {currentStep === "group_size" ? (
            <Step
              title="About how many people will attend?"
              hint="Approximate is fine — we just need a ballpark."
            >
              <input
                type="number"
                min={1}
                max={2000}
                inputMode="numeric"
                autoComplete="off"
                // Chrome occasionally misclassifies bare number inputs
                // as credit-card / payment fields and pops the card
                // autofill UI. A non-payment name + autoComplete='off'
                // + an explicit data-form-type hint dodges that.
                name="attendee_count"
                data-form-type="other"
                value={answers.group_size ?? ""}
                onChange={(e) =>
                  setAnswers((a) => ({
                    ...a,
                    group_size: e.target.value
                      ? Number(e.target.value)
                      : undefined,
                  }))
                }
                placeholder="e.g. 50"
                className="w-full rounded-md border-2 border-[#e8c9a2] bg-white px-4 py-3 text-lg text-slate-900 focus:border-[#B87333] focus:outline-none"
              />
            </Step>
          ) : null}

          {currentStep === "lead_time" ? (
            <Step title="When do you need it?">
              {renderChoice(LEAD_TIME_OPTIONS, answers.lead_time_band, (v) =>
                setAnswers((a) => ({ ...a, lead_time_band: v })),
              )}
            </Step>
          ) : null}

          {currentStep === "customization" ? (
            <Step
              title="How tailored should the content be?"
              hint="Tailoring is the single biggest price driver."
            >
              {renderChoice(CUSTOMIZATION_OPTIONS, answers.customization, (v) =>
                setAnswers((a) => ({ ...a, customization: v })),
              )}
            </Step>
          ) : null}

          {currentStep === "message" ? (
            <Step
              title="Anything else you'd like to share?"
              hint="Optional. Industry, audience makeup, goals, deadlines, etc."
            >
              <textarea
                rows={5}
                value={answers.message ?? ""}
                onChange={(e) =>
                  setAnswers((a) => ({ ...a, message: e.target.value }))
                }
                placeholder="e.g. We're a healthcare org, audience is mostly clinical leaders new to AI…"
                className="w-full rounded-md border-2 border-[#e8c9a2] bg-white px-4 py-3 text-sm text-slate-900 focus:border-[#B87333] focus:outline-none"
              />
            </Step>
          ) : null}

          {currentStep === "estimate" ? (
            <div>
              <h2 className="text-xl font-bold tracking-tight text-[#5a3614] sm:text-2xl">
                Your rough estimate
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                Heavy emphasis on <strong>rough</strong>. This is a ballpark
                based on what you told us — the exact number depends on the
                scope and the specifics of your audience.
              </p>
              <div className="mt-5 rounded-md border-2 border-[#B87333]/30 bg-gradient-to-r from-[#fdf6ec] to-[#fbe9d2] p-6 text-center">
                {loadingEstimate ? (
                  <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#8a531f]" />
                ) : estimateError ? (
                  <div className="text-sm text-rose-600">{estimateError}</div>
                ) : estimate ? (
                  <>
                    <div className="text-3xl font-extrabold tracking-tight text-[#5a3614] sm:text-4xl">
                      {currency(estimate.estimated_low)} —{" "}
                      {currency(estimate.estimated_high)}
                    </div>
                    <ul className="mt-3 space-y-1 text-left text-xs text-[#8a531f]/80">
                      {estimate.notes.map((n) => (
                        <li key={n}>· {n}</li>
                      ))}
                    </ul>
                  </>
                ) : null}
              </div>
            </div>
          ) : null}

          {currentStep === "lead" ? (
            <div>
              <h2 className="text-xl font-bold tracking-tight text-[#5a3614] sm:text-2xl">
                Want a refined quote?
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                Optional. Leave a few details and we'll follow up with a tighter
                number tailored to your situation. Or just skip and grab a slot
                on the calendar.
              </p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <LeadField
                  label="Your name"
                  value={leadName}
                  onChange={setLeadName}
                  placeholder="Jane Smith"
                />
                <LeadField
                  label="Email"
                  type="email"
                  value={leadEmail}
                  onChange={setLeadEmail}
                  placeholder="jane@example.com"
                />
                <LeadField
                  label="Company (optional)"
                  value={leadCompany}
                  onChange={setLeadCompany}
                  placeholder="Acme Healthcare"
                />
                <LeadField
                  label="Phone (optional)"
                  value={leadPhone}
                  onChange={setLeadPhone}
                  placeholder="(555) 555-5555"
                  type="tel"
                />
              </div>
              {/* Honeypot — visually hidden, bots fill it in, we drop. */}
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  left: "-10000px",
                  top: "auto",
                  width: 1,
                  height: 1,
                  overflow: "hidden",
                }}
              >
                <label>
                  Leave this blank
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                  />
                </label>
              </div>
            </div>
          ) : null}

          {/* Footer nav */}
          <div className="mt-6 flex items-center justify-between border-t-2 border-[#fdf6ec] pt-4">
            <button
              type="button"
              onClick={goBack}
              disabled={stepIndex === 0}
              className="inline-flex items-center gap-1 rounded-md border border-[#e8c9a2] bg-white px-3 py-2 text-sm font-bold text-[#8a531f] shadow-sm hover:bg-[#fdf6ec] disabled:opacity-40"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            <div className="text-xs text-slate-500">
              {stepIndex + 1} / {steps.length}
            </div>
            {currentStep === "lead" ? (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-b from-[#B87333] to-[#8a531f] px-4 py-2 text-sm font-bold text-white shadow-md disabled:opacity-50"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Mail className="h-4 w-4" />
                )}
                Send it
              </button>
            ) : (
              <button
                type="button"
                onClick={goNext}
                disabled={!canAdvance()}
                className="inline-flex items-center gap-1 rounded-md bg-gradient-to-b from-[#B87333] to-[#8a531f] px-3 py-2 text-sm font-bold text-white shadow-md disabled:opacity-40"
              >
                Next
                <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </PageShell>
  );
}

// ─── Layout pieces ──────────────────────────────────────────────────

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#fdf6ec] via-white to-white pt-16">
      <header className="border-b-2 border-[#e8c9a2] bg-white/70 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center gap-2 px-4 py-3 sm:px-6">
          <Sparkles className="h-5 w-5 text-[#B87333]" />
          <Link
            to="/"
            className="text-sm font-bold tracking-tight text-[#5a3614]"
          >
            AI Genius Automations
          </Link>
          <span className="ml-auto text-xs text-[#8a531f]/70">
            Get a rough estimate · ≈2 minutes
          </span>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-12">
        {children}
      </main>
    </div>
  );
}

function Step({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h2 className="text-xl font-bold tracking-tight text-[#5a3614] sm:text-2xl">
        {title}
      </h2>
      {hint ? <p className="mt-1 text-sm text-slate-600">{hint}</p> : null}
      <div className="mt-5">{children}</div>
    </div>
  );
}

function LeadField({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  type?: "text" | "email" | "tel";
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-[#8a531f]">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-md border-2 border-[#e8c9a2] bg-white px-3 py-2 text-sm text-slate-900 focus:border-[#B87333] focus:outline-none"
      />
    </label>
  );
}
